// src/context/ChildContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useAuth } from "./AuthContext";
import {
  differenceInYears,
  differenceInMonths,
  differenceInWeeks,
  differenceInDays,
  addMonths,
  addYears,
  addWeeks,
} from "date-fns";

const ChildContext = createContext();

export const ChildProvider = ({ children }) => {
  const { user } = useAuth();
  const [childList, setChildList] = useState([]);
  const [currentChildId, setCurrentChildId] = useState(null);
  const [currentChild, setCurrentChild] = useState(null);
  const { childId } = useParams();

  useEffect(() => {
    const fetchChildren = async () => {
      if (!user) {
        setChildList([]);
        setCurrentChildId(null);
        return;
      }

      const q = query(
        collection(db, "children"),
        where("access", "array-contains", user.email)
      );
      const querySnapshot = await getDocs(q);
      const childrenData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setChildList(childrenData);

      if (childrenData.length > 0 && !currentChildId) {
        if (childId) {
          const child = childrenData.find((child) => child.id === childId);
          if (child) {
            setCurrentChildId(child.id);
            setCurrentChild(child);
          } else {
            setCurrentChildId(childrenData[0]?.id || null);
            setCurrentChild(childrenData[0] || null);
          }
        }
      }
    };

    fetchChildren();
  }, [user, currentChildId]);

  const getChildAgeAtEntry = (birthDateString, createdAt) => {
    if (!birthDateString) return "";

    const birthDate = new Date(birthDateString);
    const now = new Date(createdAt);
    const totalDays = differenceInDays(now, birthDate);

    if (totalDays < 7 * 13) {
      const weeks = differenceInWeeks(now, birthDate);
      const days = differenceInDays(now, addWeeks(birthDate, weeks));
      return `${weeks}w ${days}d`;
    }

    if (totalDays < 365) {
      const months = differenceInMonths(now, birthDate);
      const days = differenceInDays(now, addMonths(birthDate, months));
      return `${months}m ${days}d`;
    }

    const years = differenceInYears(now, birthDate);
    const yearDate = addYears(birthDate, years);
    const months = differenceInMonths(now, yearDate);
    const days = differenceInDays(now, addMonths(yearDate, months));
    return `${years}y ${months}m ${days}d`;
  };

  const showDropBanner = () => {
    if (!currentChild) return false;

    const birthDate = new Date(currentChild.birthDate);
    const now = new Date();
    const totalDays = differenceInDays(now, birthDate);

    if (totalDays > 14) return true;

    return false;
  };

  const formatChildAge = (birthDateString) => {
    if (!birthDateString) return "";

    const birthDate = new Date(birthDateString);
    const now = new Date();
    const totalDays = differenceInDays(now, birthDate);

    if (totalDays < 7 * 13) {
      const weeks = differenceInWeeks(now, birthDate);
      const days = differenceInDays(now, addWeeks(birthDate, weeks));
      return `${weeks}w ${days}d`;
    }

    if (totalDays < 365) {
      const months = differenceInMonths(now, birthDate);
      const days = differenceInDays(now, addMonths(birthDate, months));
      return `${months}m ${days}d`;
    }

    const years = differenceInYears(now, birthDate);
    const yearDate = addYears(birthDate, years);
    const months = differenceInMonths(now, yearDate);
    const days = differenceInDays(now, addMonths(yearDate, months));
    return `${years}y ${months}m ${days}d`;
  };

  const leaps = [
    {
      id: 1,
      title: "Leap 1: Changing Sensations",
      description: `
        <p>At around 5 weeks, your baby becomes more alert to the world and sensations feel new and sometimes overwhelming.</p>
        <ul>
          <li>More alert and awake</li>
          <li>Recognizes familiar voices</li>
          <li>Starts to focus on faces</li>
        </ul>
        <p>Expect more crying, clinginess, and shorter naps as your baby adjusts to stronger sensory input.</p>`,
      week: 5,
    },
    {
      id: 2,
      title: "Leap 2: Patterns",
      description: `
        <p>Your baby starts to notice simple patterns in light, sound, touch, and movement.</p>
        <ul>
          <li>Starts to recognize patterns</li>
          <li>More intense body movements</li>
          <li>Begins to coo and smile more</li>
        </ul>
        <p>Clinginess may increase as your baby realizes the world is changing fast.</p>`,
      week: 8,
    },
    {
      id: 3,
      title: "Leap 3: Smooth Transitions",
      description: `
        <p>Now your baby begins to perceive smooth changes — like watching a moving object or shifting tones in voices.</p>
        <ul>
          <li>Can follow objects with eyes</li>
          <li>Explores with hands and mouth</li>
          <li>Shows interest in surroundings</li>
        </ul>
        <p>This leap supports better motor control and social responsiveness.</p>`,
      week: 12,
    },
    {
      id: 4,
      title: "Leap 4: Events",
      description: `
        <p>Your baby starts to recognize simple events — for example, knowing that when a toy drops, it falls.</p>
        <ul>
          <li>Starts to grab and hold toys</li>
          <li>Laughs out loud</li>
          <li>Practices rolling over</li>
        </ul>
        <p>Sleep may regress as the brain is busy processing new cause-and-effect events.</p>`,
      week: 19,
    },
    {
      id: 5,
      title: "Leap 5: Relationships",
      description: `
        <p>This leap is about understanding the relationships between people and things — like distance and object permanence.</p>
        <ul>
          <li>Understands distance and perspective</li>
          <li>May experience separation anxiety</li>
          <li>Reaches out to be picked up</li>
        </ul>
        <p>Expect a clingier baby during this leap — they may cry when you leave the room.</p>`,
      week: 26,
    },
    {
      id: 6,
      title: "Leap 6: Categories",
      description: `
        <p>Your baby starts grouping things by shape, size, texture, and function.</p>
        <ul>
          <li>Begins to explore categories (e.g. shapes, textures)</li>
          <li>Imitates sounds and facial expressions</li>
          <li>Sits with little or no support</li>
        </ul>
        <p>This leap sets the foundation for language and object recognition.</p>`,
      week: 37,
    },
    {
      id: 7,
      title: "Leap 7: Sequences",
      description: `
        <p>Now your baby understands sequences — that things happen in a particular order.</p>
        <ul>
          <li>Understands sequences (e.g. stacking blocks)</li>
          <li>Shows preferences and emotions more clearly</li>
          <li>May begin crawling or pulling to stand</li>
        </ul>
        <p>Routines become more important, and transitions can cause frustration.</p>`,
      week: 46,
    },
    {
      id: 8,
      title: "Leap 8: Programs",
      description: `
        <p>Babies now begin to understand simple programs — predictable steps in routines like bath time or bedtime.</p>
        <ul>
          <li>Understands the concept of programs (e.g. routines)</li>
          <li>May start to use simple words</li>
          <li>Develops stronger social awareness</li>
        </ul>
        <p>This leap boosts memory and anticipation of what comes next.</p>`,
      week: 55,
    },
    {
      id: 9,
      title: "Leap 9: Principles",
      description: `
        <p>Your toddler now begins to understand rules, fairness, and basic cause and effect.</p>
        <ul>
          <li>Starts to understand principles (e.g. fairness, cause and effect)</li>
          <li>Shows more independence</li>
          <li>Can follow simple instructions</li>
        </ul>
        <p>This is often when you’ll see early attempts at negotiation and testing limits.</p>`,
      week: 60,
    },
    {
      id: 10,
      title: "Leap 10: Systems",
      description: `
        <p>This final leap involves grasping complex systems and how parts work together — both physically and socially.</p>
        <ul>
          <li>Understands systems (how things work together)</li>
          <li>Plays more complex pretend games</li>
          <li>Communicates clearly with gestures and words</li>
        </ul>
        <p>You may notice big leaps in problem-solving, role play, and verbal expression.</p>`,
      week: 75,
    },
  ];

  const getLeapStatus = (birthDateString) => {
    if (!birthDateString) return {};

    const birthDate = new Date(birthDateString);
    const now = new Date();
    const ageInDays = differenceInDays(now, birthDate);
    const ageInWeeks = ageInDays / 7;
    const leapDuration = 0;

    // Find the most recent leap that has ended
    const previousLeap = [...leaps]
      .reverse()
      .find((leap) => ageInWeeks >= leap.week + leapDuration);

    // Calculate days since last leap ended
    let daysSinceLastLeap = null;
    if (previousLeap) {
      const leapEndDate = addWeeks(birthDate, previousLeap.week + leapDuration);
      daysSinceLastLeap = differenceInDays(now, leapEndDate);
    }

    // Next leap is the first not yet started
    const nextLeap = leaps.find((leap) => leap.week > ageInWeeks);

    let daysUntilNext = null;
    if (nextLeap) {
      const nextLeapDate = addWeeks(birthDate, nextLeap.week);
      daysUntilNext = differenceInDays(nextLeapDate, now);
    }

    return {
      previousLeap, // last completed leap
      daysSinceLastLeap, // how long ago it ended
      nextLeap,
      daysUntilNext,
      leaps,
    };
  };

  return (
    <ChildContext.Provider
      value={{
        childList,
        currentChildId,
        setCurrentChildId,
        currentChild,
        formatChildAge,
        getChildAgeAtEntry,
        getLeapStatus,
        showDropBanner,
      }}
    >
      {children}
    </ChildContext.Provider>
  );
};

export const useChild = () => useContext(ChildContext);
